import { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { SingleSelectCards, StepNavigation } from '../../components/quiz';
import { Button } from '../../components/ui';
import { track, identify, page, EVENTS } from '../../utils/analytics';
import { buildTaxesSignupUrl } from '../../utils/signupUrl';
import { validateEmail } from '../../utils/validation';
import { useApp } from '../../contexts/AppContext';
import type { SelectableCardOption } from '../../types';

// ── Types ─────────────────────────────────────────────────────────────────────

type Phase = 'intro' | 'context' | 'quiz' | 'narration' | 'insight1' | 'insight2' | 'loading' | 'email' | 'results';


interface Answers {
  q1?: string; // segmentation
  q2?: string; // income consistency
  q3?: string; // tax season (present tense)
  q4?: string; // tax knowledge
  q5?: string; // tax account
  q6?: string; // set-aside method
  q7?: string; // consistency
  q8?: string; // business/personal separation
  q9?: string; // time on admin
  q10?: string; // goal
}

// ── Scoring ────────────────────────────────────────────────────────────────────
// Q1: segmentation, not scored
// Q2-Q9: 0-3 pts each = 0-24
// Q10: 1-3 pts
// Total: 0-27

const SCORES: Record<string, Record<string, number>> = {
  q2: { VERY_CONSISTENT: 0, MOSTLY_CONSISTENT: 1, VARIES_NOTICEABLY: 2, HIGHLY_VARIABLE: 3 },
  q3: { CALM_READY: 0, MOSTLY_FINE: 1, SURVIVABLE: 2, STRESSED: 3 },
  q4: { KNOW_RATE: 0, HAVE_NUMBER: 1, ROUGH_GUESS: 2, NO_IDEA: 3 },
  q5: { DEDICATED_ACCOUNT: 0, SEPARATE_SAVINGS: 1, MOVE_WHEN_REMEMBER: 2, MAIN_ACCOUNT: 3 },
  q6: { AUTOMATIC: 0, MANUAL_CALC: 1, ROUGH_NUMBER: 2, FIGURE_OUT_LATER: 3 },
  q7: { EVERY_TIME: 0, MOST_OF_TIME: 1, SOMETIMES: 2, RARELY_NEVER: 3 },
  q8: { COMPLETELY_SEPARATED: 0, MOSTLY_SEPARATED: 1, SOMEWHAT_SEPARATED: 2, FULLY_MIXED: 3 },
  q9: { ALMOST_NONE: 0, UNDER_HOUR: 1, FEW_HOURS: 2, DONT_TRACK: 3 },
  q10: { KNOW_WHAT_OWE: 1, NEVER_WORRY: 2, CLEAN_ORGANIZED: 2, FULL_AUTOMATION: 3 },
};

function getScore(q: number, answerId: string): number {
  return SCORES[`q${q}`]?.[answerId] ?? 0;
}

function getTotalScore(answers: Answers): number {
  return Object.entries(answers)
    .filter(([key]) => key !== 'q1')
    .reduce((sum, [key, val]) => {
      const q = parseInt(key.replace('q', ''));
      return sum + (val ? getScore(q, val) : 0);
    }, 0);
}

// ── Question definitions ────────────────────────────────────────────────────────

const Q1_OPTIONS: SelectableCardOption[] = [
  { id: 'BUSINESS', icon: '🏢', title: 'I run a business', description: '' },
  { id: 'SIDE_HUSTLE', icon: '⚡', title: 'I have a side hustle', description: 'Alongside my main job' },
  { id: 'MULTI_INCOME', icon: '🔀', title: 'I have multiple income sources', description: 'Gig work, rentals, investments, or a mix' },
  { id: 'FLUCTUATING', icon: '📈', title: 'My income fluctuates a lot', description: "Freelance, seasonal, or project-based" },
  { id: 'EMPLOYED', icon: '💼', title: "I'm fully employed", description: 'W-2 with a regular paycheck' },
];

const Q2_OPTIONS: SelectableCardOption[] = [
  { id: 'VERY_CONSISTENT', icon: '📅', title: 'Very predictable', description: 'Almost the same every month' },
  { id: 'MOSTLY_CONSISTENT', icon: '📊', title: 'Mostly predictable', description: '' },
  { id: 'VARIES_NOTICEABLY', icon: '📈', title: 'It varies a lot', description: '' },
  { id: 'HIGHLY_VARIABLE', icon: '🎢', title: 'I never really know', description: '' },
];

const Q3_OPTIONS: SelectableCardOption[] = [
  { id: 'STRESSED', icon: '😰', title: 'Stressful and expensive', description: "I'm not prepared and it shows" },
  { id: 'SURVIVABLE', icon: '😅', title: 'Stressful but manageable', description: "I get through it but it's not fun" },
  { id: 'MOSTLY_FINE', icon: '😌', title: 'Mostly fine', description: 'I have most of what I need' },
  { id: 'CALM_READY', icon: '🧘', title: 'Calm and under control', description: "I have everything set aside - it's straightforward" },
];

const Q4_OPTIONS: SelectableCardOption[] = [
  { id: 'NO_IDEA', icon: '🤷', title: "Honestly, no - I'm not sure where to start", description: '' },
  { id: 'ROUGH_GUESS', icon: '🎯', title: "I have a rough guess but I'm not very confident", description: '' },
  { id: 'HAVE_NUMBER', icon: '🔢', title: "I have a number I use, but I'm not sure it's right", description: 'For my situation' },
  { id: 'KNOW_RATE', icon: '✅', title: 'Yes - I know my effective rate', description: 'And I save accordingly' },
];

const Q5_OPTIONS: SelectableCardOption[] = [
  { id: 'MAIN_ACCOUNT', icon: '😬', title: 'In my main account', description: "I try to remember not to spend it" },
  { id: 'MOVE_WHEN_REMEMBER', icon: '🔄', title: 'I move it to savings when I remember to', description: '' },
  { id: 'SEPARATE_SAVINGS', icon: '🏦', title: 'I have a separate savings account for taxes', description: 'I try to use it consistently' },
  { id: 'DEDICATED_ACCOUNT', icon: '🔒', title: 'I have a dedicated tax-only account', description: 'It never mixes with anything else' },
];

const Q6_OPTIONS: SelectableCardOption[] = [
  { id: 'FIGURE_OUT_LATER', icon: '🙈', title: "I don't set anything aside", description: '' },
  { id: 'ROUGH_NUMBER', icon: '🧮', title: 'I estimate and move something manually', description: '' },
  { id: 'MANUAL_CALC', icon: '✏️', title: 'I calculate and move a percentage', description: '' },
  { id: 'AUTOMATIC', icon: '🤖', title: 'It happens automatically', description: 'A rule does it for me' },
];

const Q7_OPTIONS: SelectableCardOption[] = [
  { id: 'RARELY_NEVER', icon: '❌', title: 'Rarely or never', description: 'I handle it all at once when tax time arrives' },
  { id: 'SOMETIMES', icon: '⚡', title: 'Sometimes', description: 'When I remember or have a clear surplus' },
  { id: 'MOST_OF_TIME', icon: '✔️', title: "Most of the time - I'm fairly consistent", description: '' },
  { id: 'EVERY_TIME', icon: '🏆', title: 'Every time income arrives', description: 'Without exception' },
];

const Q8_OPTIONS_STANDARD: SelectableCardOption[] = [
  { id: 'FULLY_MIXED', icon: '🗂️', title: 'Fully mixed', description: 'One account handles everything' },
  { id: 'SOMEWHAT_SEPARATED', icon: '🔀', title: 'Somewhat separated', description: 'I try to keep them apart but it gets messy' },
  { id: 'MOSTLY_SEPARATED', icon: '📂', title: 'Mostly separated', description: 'Different accounts, mostly clean' },
  { id: 'COMPLETELY_SEPARATED', icon: '✅', title: 'Completely separated', description: 'Business and personal money never mix' },
];

const Q8_OPTIONS_EMPLOYED: SelectableCardOption[] = [
  { id: 'FULLY_MIXED', icon: '🗂️', title: 'Fully mixed', description: 'One account handles everything' },
  { id: 'SOMEWHAT_SEPARATED', icon: '🔀', title: 'Somewhat separated', description: 'I try to keep savings apart but it gets messy' },
  { id: 'MOSTLY_SEPARATED', icon: '📂', title: 'Mostly separated', description: 'Different accounts for different goals' },
  { id: 'COMPLETELY_SEPARATED', icon: '✅', title: 'Completely separated', description: 'Savings goals never mix with day-to-day spending' },
];

const Q9_OPTIONS: SelectableCardOption[] = [
  { id: 'DONT_TRACK', icon: '🌀', title: "I don't really track it", description: 'Tasks pile up and I deal with them when I have to' },
  { id: 'FEW_HOURS', icon: '🕐', title: 'A few hours', description: "Manual but I get it done" },
  { id: 'UNDER_HOUR', icon: '⚡', title: 'Under an hour', description: 'I have some things automated' },
  { id: 'ALMOST_NONE', icon: '🤖', title: 'Almost none', description: 'My system handles it without me' },
];

const Q10_OPTIONS: SelectableCardOption[] = [
  { id: 'KNOW_WHAT_OWE', icon: '🔢', title: 'Knowing exactly what I owe', description: '' },
  { id: 'NEVER_WORRY', icon: '😌', title: "Not worrying if I saved enough", description: '' },
  { id: 'CLEAN_ORGANIZED', icon: '📋', title: 'Having everything clean for my accountant', description: '' },
  { id: 'FULL_AUTOMATION', icon: '🤖', title: 'Having it all handled automatically', description: '' },
];

// ── Narration copy ─────────────────────────────────────────────────────────────

function getNarration(q: number, answerId: string): string {
  switch (q) {
    case 2:
      if (answerId === 'VERY_CONSISTENT') return "Predictable income hides weak systems. Everything feels fine - until it isn't.";
      if (answerId === 'MOSTLY_CONSISTENT') return "Your variability is small - but it still leaks money. Those gaps add up over time.";
      if (answerId === 'VARIES_NOTICEABLY') return "Your income changes, but your system probably doesn't. That's where things break.";
      return "You can't run finances on fixed numbers. Unpredictable income needs a different system.";

    case 3:
      if (answerId === 'CALM_READY') return "You've felt what \"working\" looks like. The question is whether it's a system, or just a good year.";
      if (answerId === 'MOSTLY_FINE') return "You're close, but not stable. A weaker year would expose the gaps.";
      if (answerId === 'SURVIVABLE') return "Managing stress isn't the goal. It means the system is still breaking under pressure.";
      return "That wasn't bad luck. It was months of not setting money aside.";

    case 4:
      if (answerId === 'NO_IDEA') return "Most self-employed people skip this step. The ones who figure it out usually only have to do it once.";
      if (answerId === 'ROUGH_GUESS') return "A 5% miss on $80k of income is $4,000 - either paid as a penalty or locked up for no reason.";
      if (answerId === 'HAVE_NUMBER') return "Having a number is most of the work. The question is whether it still fits your situation, or your old one.";
      return "Good. Now the question is whether that rate is applied automatically - or only when you remember to.";

    case 5:
      if (answerId === 'MAIN_ACCOUNT') return "That money is already spent. Not recklessly - it blends in, and blended money gets used.";
      if (answerId === 'MOVE_WHEN_REMEMBER') return "The months you forget are your highest-income months. That's not a coincidence - it's when you're busiest.";
      if (answerId === 'SEPARATE_SAVINGS') return "Right instinct. Now: does the money move itself, or wait for you to move it?";
      return "Strong foundation. We'll check whether the system feeding that account is just as reliable.";

    case 6:
      if (answerId === 'FIGURE_OUT_LATER') return "Year-end bills are almost always bigger than expected. Splitting it into 12 smaller saves is just math.";
      if (answerId === 'ROUGH_NUMBER') return "A mental number is a start. The gap between 'roughly' and 'exactly' is where hundreds quietly disappear each year.";
      if (answerId === 'MANUAL_CALC') return "Manual works until you're busy. And the months you're busiest are exactly when you earn the most.";
      return "That's the right setup. We'll look at how well-calibrated and consistently triggered it actually is.";

    case 7:
      if (answerId === 'RARELY_NEVER') return "Tax bills that feel like surprises almost never are. They're the sum of months where nothing got set aside.";
      if (answerId === 'SOMETIMES') return "The months you skip are your highest-income months. That's when the gap compounds fastest.";
      if (answerId === 'MOST_OF_TIME') return "Almost every time is genuinely great. The value of 100% is mostly psychological - but it's real.";
      return "That's a real system. We'll look at whether everything around it is just as solid.";

    case 8:
      if (answerId === 'FULLY_MIXED') return "Mixed finances make it nearly impossible to know your real tax number. Clean separation is usually a one-time fix.";
      if (answerId === 'SOMEWHAT_SEPARATED') return "Messy separation is the most common audit trigger. Deductions need a clean paper trail to hold up.";
      if (answerId === 'MOSTLY_SEPARATED') return "Good foundation. The goal is statements your accountant can read without asking you to reconstruct anything.";
      return "That's the mark of a clean financial life. Everything downstream - taxes, bookkeeping, CPA work - gets simpler from here.";

    case 9:
      if (answerId === 'DONT_TRACK') return "Financial tasks that pile up don't disappear - they compound. Untangling them later always takes longer.";
      if (answerId === 'FEW_HOURS') return "A few hours a month is days each year. Most people would rather spend that on work that actually earns.";
      if (answerId === 'UNDER_HOUR') return "Under an hour is solid. The cleanest setups run near zero - not through discipline, but through automation.";
      return "That's the goal. We'll show your full score in just a moment.";

    case 10:
      if (answerId === 'KNOW_WHAT_OWE') return "That clarity starts with your rate and a system that applies it automatically every time income arrives.";
      if (answerId === 'NEVER_WORRY') return "That peace of mind is exactly what a well-built system delivers. Not just savings - confidence you saved enough.";
      if (answerId === 'CLEAN_ORGANIZED') return "A clean handoff saves you time, saves your accountant time, and often reduces your bill.";
      return "Your score is ready.";

    default:
      return '';
  }
}

// ── Tier (result) definitions ─────────────────────────────────────────────────
// Max score: 8 questions x 3 pts + Q10 max 3 pts = 27
// Tiers: 20-27 / 10-19 / 0-9

interface Tier {
  scoreMin: number;
  scoreMax: number;
  color: 'green' | 'yellow' | 'orange' | 'red';
  name: string;
  diagnosisHeadline: string;
  diagnosisBody: string[];
  promoIntro: string;
  promoDesc: string;
  promoTagline: string;
  promoBullets: string[];
  promoCta: string;
}

const TIERS: Tier[] = [
  {
    scoreMin: 20, scoreMax: 27, color: 'green',
    name: 'Stable But Fragile',
    diagnosisHeadline: "Things work, but only when everything goes right.",
    diagnosisBody: [
      "You've experienced what \"good\" feels like. But it depends on consistency, not a system. A weaker month or surprise expense would expose the gaps.",
      "This isn't about doing more. It's about making your setup hold up in any scenario.",
      "Right now, your finances work in good conditions, not under pressure.",
    ],
    promoIntro: "There's a better way to run your finances",
    promoDesc: "Sequence runs your finances programmatically, moving money automatically based on rules that adapt to your income.",
    promoTagline: "Money moves before you can spend it.",
    promoBullets: [
      "Taxes are set aside automatically",
      "Expenses are separated upfront",
      "Your system holds even in bad months",
    ],
    promoCta: "Make it bulletproof",
  },
  {
    scoreMin: 13, scoreMax: 19, color: 'yellow',
    name: 'Almost There',
    diagnosisHeadline: "You're close, but money leaks.",
    diagnosisBody: [
      "You have some structure. But small inconsistencies are quietly adding up. Your good months are compensating for weaker ones.",
      "That's not discipline - it's lack of automation.",
      "This keeps you stuck in the same place even when income grows.",
    ],
    promoIntro: "There's a better way to run your finances",
    promoDesc: "Sequence runs your finances programmatically, moving money automatically based on rules that adapt to your income.",
    promoTagline: "Money moves before you can spend it.",
    promoBullets: [
      "Income is allocated automatically",
      "Gaps between months are removed",
      "You actually retain progress",
    ],
    promoCta: "Fix the gaps",
  },
  {
    scoreMin: 6, scoreMax: 12, color: 'orange',
    name: 'Strained System',
    diagnosisHeadline: "Your system breaks when things change.",
    diagnosisBody: [
      "Your income varies - but your setup doesn't adjust with it. So you're constantly reacting, shifting, and catching up.",
      "Manual systems don't work with variable income.",
      "That's why things feel manageable - but never stable.",
    ],
    promoIntro: "There's a better way to run your finances",
    promoDesc: "Sequence runs your finances programmatically - adapting automatically to each income event.",
    promoTagline: "Your system adjusts as money comes in.",
    promoBullets: [
      "Allocations adjust with every deposit",
      "No more catching up between months",
      "Stability without needing consistency",
    ],
    promoCta: "Make it adapt",
  },
  {
    scoreMin: 0, scoreMax: 5, color: 'red',
    name: 'Breaking / Reactive',
    diagnosisHeadline: "You don't have a system. You're reacting.",
    diagnosisBody: [
      "Money comes in, decisions happen in the moment, and taxes or expenses show up later as pressure.",
      "That's not chaos - it's what happens without structure.",
      "This is why things feel expensive, unpredictable, and hard to get ahead of.",
    ],
    promoIntro: "There's a better way to run your finances",
    promoDesc: "Sequence runs your finances programmatically, handling money before it becomes a decision.",
    promoTagline: "Money is organized before you can spend it.",
    promoBullets: [
      "Taxes are handled before they become a problem",
      "Spending is separated automatically",
      "You stop reacting to money",
    ],
    promoCta: "Take control now",
  },
];

function getTier(score: number): Tier {
  return TIERS.find((t) => score >= t.scoreMin && score <= t.scoreMax) ?? TIERS[TIERS.length - 1];
}

// Score 0-27 across 4 tiers: 20-27 / 13-19 / 6-12 / 0-5

// ── Personalized gaps ──────────────────────────────────────────────────────────

const GAP_MESSAGES: Record<string, Record<string, string>> = {
  q5: {
    MAIN_ACCOUNT: 'Tax money in your spending account gets spent - not from recklessness, just proximity.',
    MOVE_WHEN_REMEMBER: 'Your highest-income months are your busiest - exactly when you forget to move money.',
  },
  q6: {
    FIGURE_OUT_LATER: 'Every month without a set-aside is a gap that compounds until April.',
    ROUGH_NUMBER: 'A rough estimate means your best months are almost certainly under-saving.',
    MANUAL_CALC: 'Manual calculations break in your busiest, highest-earning months - when it matters most.',
  },
  q7: {
    RARELY_NEVER: 'Tax bills that feel like surprises are the sum of months where nothing got set aside.',
    SOMETIMES: 'The months you skip are almost always your highest-income months.',
  },
  q4: {
    NO_IDEA: "Without knowing your effective rate, every set-aside is a guess - and guesses cost money at filing.",
    ROUGH_GUESS: 'A 5% rate miss on $80k of income is $4,000 either owed as a penalty or locked up unnecessarily.',
  },
  q8: {
    FULLY_MIXED: 'Mixed finances make your real tax number invisible and deductions hard to defend.',
    SOMEWHAT_SEPARATED: 'Messy separation is the most common audit trigger.',
  },
  q2: {
    HIGHLY_VARIABLE: 'Variable income needs a percentage rule, not a fixed amount - so good months do more work.',
    VARIES_NOTICEABLY: 'A fixed set-aside under-saves in good months and over-locks in slow ones.',
  },
  q9: {
    DONT_TRACK: 'Financial admin that piles up compounds - untangling later always costs more.',
    FEW_HOURS: 'Hours of manual admin per month is days per year on work that should run automatically.',
  },
};

function getPersonalizedGaps(answers: Answers): string[] {
  const priority = ['q5', 'q6', 'q7', 'q4', 'q8', 'q2', 'q9'];
  const scored: Array<{ score: number; message: string }> = [];
  for (const qKey of priority) {
    const answerId = answers[qKey as keyof Answers];
    if (!answerId) continue;
    const qNum = parseInt(qKey.replace('q', ''));
    const s = getScore(qNum, answerId);
    if (s === 0) continue;
    const msg = GAP_MESSAGES[qKey]?.[answerId];
    if (!msg) continue;
    scored.push({ score: s, message: msg });
  }
  scored.sort((a, b) => b.score - a.score);
  return scored.slice(0, 3).map((s) => s.message);
}

// ── Loading checklist ──────────────────────────────────────────────────────────

const LOADING_ITEMS = [
  'Income complexity reviewed',
  'Current separation habits analyzed',
  'Consistency patterns assessed',
  'Building your personalized score...',
];

// ── Analytics question keys ────────────────────────────────────────────────────

const QUESTION_KEYS: Record<number, string> = {
  1: 'segment',
  2: 'income_consistency',
  3: 'tax_season_feeling',
  4: 'tax_knowledge',
  5: 'tax_account_location',
  6: 'set_aside_method',
  7: 'consistency',
  8: 'business_personal_separation',
  9: 'time_on_admin',
  10: 'goal_framing',
};

const TOTAL_QUESTIONS = 10;

function getDevice(): 'mobile' | 'tablet' | 'desktop' {
  const w = window.innerWidth;
  if (w < 768) return 'mobile';
  if (w < 1024) return 'tablet';
  return 'desktop';
}

// ── Question content resolver ─────────────────────────────────────────────────

function getQuestionContent(
  q: number,
  answers: Answers
): { question: string; helper?: string; options: SelectableCardOption[] } {
  const isEmployed = answers.q1 === 'EMPLOYED';

  switch (q) {
    case 1:
      return { question: 'Which best describes your income?', options: Q1_OPTIONS };
    case 2:
      return { question: 'How predictable is your income?', options: Q2_OPTIONS };
    case 3:
      return { question: 'How do you feel about tax season right now?', options: Q3_OPTIONS };
    case 4:
      return { question: 'Do you know what percentage of your income should go toward taxes?', options: Q4_OPTIONS };
    case 5:
      return { question: 'Where does your tax money actually live right now?', options: Q5_OPTIONS };
    case 6:
      return { question: 'When money comes in, what happens?', options: Q6_OPTIONS };
    case 7:
      return { question: 'How often do you actually set money aside for taxes?', options: Q7_OPTIONS };
    case 8:
      return {
        question: isEmployed
          ? 'How separated are your savings goals from your day-to-day spending?'
          : 'How cleanly separated are your business and personal finances?',
        options: isEmployed ? Q8_OPTIONS_EMPLOYED : Q8_OPTIONS_STANDARD,
      };
    case 9:
      return {
        question: 'How much time do you spend each month on financial admin?',
        helper: 'Moving money, checking balances, calculating what to set aside',
        options: Q9_OPTIONS,
      };
    case 10:
      return { question: 'What would feel like a win this tax season?', options: Q10_OPTIONS };
    default:
      return { question: '', options: [] };
  }
}

// ── Insight illustrations ──────────────────────────────────────────────────────

function Insight1Illustration() {
  return (
    <svg width="120" height="96" viewBox="0 0 120 96" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      {/* Document */}
      <rect x="20" y="8" width="56" height="72" rx="6" fill="#f3f0ff" stroke="#7c3aed" strokeWidth="2"/>
      <rect x="30" y="22" width="36" height="3" rx="1.5" fill="#c4b5fd"/>
      <rect x="30" y="30" width="28" height="3" rx="1.5" fill="#c4b5fd"/>
      <rect x="30" y="38" width="32" height="3" rx="1.5" fill="#c4b5fd"/>
      {/* Gap indicator */}
      <rect x="30" y="50" width="36" height="12" rx="3" fill="#fef3c7" stroke="#f59e0b" strokeWidth="1.5"/>
      <text x="48" y="60" textAnchor="middle" fill="#d97706" fontSize="9" fontWeight="700">+30%</text>
      {/* Arrow up */}
      <path d="M86 64 L86 32" stroke="#ef4444" strokeWidth="2.5" strokeLinecap="round"/>
      <path d="M79 40 L86 32 L93 40" stroke="#ef4444" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
      {/* Coin stack */}
      <ellipse cx="95" cy="76" rx="11" ry="4" fill="#fbbf24"/>
      <rect x="84" y="68" width="22" height="8" rx="2" fill="#fbbf24"/>
      <ellipse cx="95" cy="68" rx="11" ry="4" fill="#fcd34d"/>
    </svg>
  );
}

function Insight2Illustration() {
  // Income → AUTO RULE → [TAX POD | SPENDING]
  // Communicates: money splits automatically before you can touch it
  return (
    <svg width="220" height="108" viewBox="0 0 220 108" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      {/* INCOME label box */}
      <rect x="76" y="0" width="68" height="28" rx="14" fill="#ede9fe" stroke="#7c3aed" strokeWidth="1.5"/>
      <text x="110" y="9" textAnchor="middle" fill="#7c3aed" fontSize="7.5" fontWeight="600" fontFamily="system-ui, sans-serif">INCOME</text>
      {/* Dollar sign */}
      <line x1="110" y1="12" x2="110" y2="22" stroke="#7c3aed" strokeWidth="1.5" strokeLinecap="round"/>
      <path d="M107 14 Q106 13 107.5 12.5 C109.5 12 112.5 12 112.5 14 Q112.5 16 110 16 Q107.5 16 107.5 18 C107.5 20 110 20.5 112.5 20 Q114 19.5 113 18.5" stroke="#7c3aed" strokeWidth="1.2" strokeLinecap="round" fill="none"/>

      {/* Vertical connector */}
      <line x1="110" y1="28" x2="110" y2="42" stroke="#7c3aed" strokeWidth="1.5"/>

      {/* AUTO RULE pill */}
      <rect x="82" y="42" width="56" height="22" rx="11" fill="#7c3aed"/>
      {/* Lightning bolt inside pill */}
      <path d="M107 47 L104 55 L109.5 55 L107 63 L114 53 L108.5 53 L111 47 Z" fill="white"/>

      {/* Fork: left to TAX POD */}
      <path d="M96 64 L56 80" stroke="#22c55e" strokeWidth="2" strokeLinecap="round"/>
      {/* Fork: right to SPENDING */}
      <path d="M124 64 L164 80" stroke="#d1d5db" strokeWidth="1.5" strokeLinecap="round"/>

      {/* TAX box - green, protected */}
      <rect x="4" y="80" width="100" height="26" rx="6" fill="#f0fdf4" stroke="#22c55e" strokeWidth="1.5"/>
      {/* Lock icon - centered with label */}
      <path d="M34 89 Q34 85 40 85 Q46 85 46 89 L46 91 L34 91 Z" stroke="#22c55e" strokeWidth="1.3" fill="none" strokeLinejoin="round"/>
      <rect x="32" y="91" width="16" height="11" rx="2" fill="none" stroke="#22c55e" strokeWidth="1.3"/>
      <circle cx="40" cy="97" r="1.5" fill="#22c55e"/>
      <text x="52" y="97" fill="#16a34a" fontSize="8" fontWeight="700" fontFamily="system-ui, sans-serif">TAX</text>

      {/* SPENDING box - grey, secondary */}
      <rect x="116" y="80" width="100" height="26" rx="6" fill="#f9fafb" stroke="#e5e7eb" strokeWidth="1.5"/>
      {/* Wallet icon - centered with label */}
      <rect x="129" y="88" width="14" height="11" rx="2" stroke="#9ca3af" strokeWidth="1.3" fill="none"/>
      <line x1="129" y1="93" x2="143" y2="93" stroke="#9ca3af" strokeWidth="1"/>
      <circle cx="140" cy="96" r="1.5" fill="#9ca3af"/>
      <text x="147" y="97" fill="#9ca3af" fontSize="8" fontWeight="600" fontFamily="system-ui, sans-serif">SPENDING</text>
    </svg>
  );
}

// ── Intro illustration ─────────────────────────────────────────────────────────

function IntroIllustration() {
  return (
    <svg width="148" height="128" viewBox="0 0 148 128" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      {/* Background glow circle */}
      <circle cx="60" cy="72" r="56" fill="#ede9fe"/>

      {/* Calendar shadow */}
      <rect x="16" y="31" width="90" height="80" rx="12" fill="rgba(0,0,0,0.07)"/>

      {/* Calendar body */}
      <rect x="14" y="28" width="90" height="80" rx="12" fill="white"/>

      {/* Calendar header (purple, rounded top only) */}
      <rect x="14" y="28" width="90" height="30" rx="12" fill="#7c3aed"/>
      {/* Square off bottom half of header radius */}
      <rect x="14" y="44" width="90" height="14" fill="#7c3aed"/>

      {/* Binding rings */}
      <circle cx="38" cy="28" r="5" fill="white" stroke="#6d28d9" strokeWidth="1.5"/>
      <circle cx="80" cy="28" r="5" fill="white" stroke="#6d28d9" strokeWidth="1.5"/>

      {/* APRIL text */}
      <text x="59" y="44" textAnchor="middle" fill="white" fontSize="10.5" fontWeight="700"
            fontFamily="system-ui, -apple-system, sans-serif" letterSpacing="1.5">APRIL</text>

      {/* 15 */}
      <text x="59" y="91" textAnchor="middle" fill="#111827" fontSize="38" fontWeight="900"
            fontFamily="system-ui, -apple-system, sans-serif">15</text>

      {/* Smile */}
      <path d="M42 101 Q59 113 76 101" stroke="#22c55e" strokeWidth="2.5" strokeLinecap="round" fill="none"/>

      {/* Shield — overlapping top-right of calendar */}
      <path d="M116 22 L134 31 L134 52 Q134 65 116 71 Q98 65 98 52 L98 31 Z" fill="#ede9fe"/>
      <path d="M116 26 L131 34 L131 52 Q131 63 116 68 Q101 63 101 52 L101 34 Z"
            fill="none" stroke="#7c3aed" strokeWidth="2.2" strokeLinejoin="round"/>
      {/* Checkmark inside shield */}
      <path d="M108 50 L114 56 L126 43" stroke="#7c3aed" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

// ── Contextual narration icons ─────────────────────────────────────────────────

function getNarrationIcon(q: number): JSX.Element {
  const props = { width: 28, height: 28, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round" as const, strokeLinejoin: "round" as const, "aria-hidden": true };

  switch (q) {
    case 2: // Income consistency - equal bars (stable/steady)
      return (
        <svg {...props}>
          <line x1="2" y1="20" x2="22" y2="20" />
          <rect x="3" y="9" width="5" height="11" rx="1" fill="currentColor" stroke="none" />
          <rect x="9.5" y="9" width="5" height="11" rx="1" fill="currentColor" stroke="none" />
          <rect x="16" y="9" width="5" height="11" rx="1" fill="currentColor" stroke="none" />
        </svg>
      );
    case 3: // Tax season - calendar
      return (
        <svg {...props}>
          <rect x="3" y="4" width="18" height="18" rx="2" />
          <line x1="3" y1="10" x2="21" y2="10" />
          <line x1="8" y1="2" x2="8" y2="6" />
          <line x1="16" y1="2" x2="16" y2="6" />
          <path d="M8 14 L10.5 16.5 L16 13" />
        </svg>
      );
    case 4: // Tax knowledge - percentage
      return (
        <svg {...props}>
          <circle cx="12" cy="12" r="9" />
          <line x1="8.5" y1="15.5" x2="15.5" y2="8.5" />
          <circle cx="9" cy="9" r="1" fill="currentColor" stroke="none" />
          <circle cx="15" cy="15" r="1" fill="currentColor" stroke="none" />
        </svg>
      );
    case 5: // Tax account - vault/safe
      return (
        <svg {...props}>
          <rect x="3" y="3" width="18" height="18" rx="2" />
          <circle cx="12" cy="12" r="4" />
          <circle cx="12" cy="12" r="1.5" fill="currentColor" stroke="none" />
          <line x1="16" y1="4" x2="16" y2="6" />
          <line x1="16" y1="18" x2="16" y2="20" />
        </svg>
      );
    case 6: // Set-aside method - coin dropping into savings box
      return (
        <svg {...props}>
          <circle cx="12" cy="5" r="3" />
          <line x1="12" y1="8" x2="12" y2="13" />
          <polyline points="9 11 12 14 15 11" />
          <rect x="4" y="14" width="16" height="7" rx="2" />
          <line x1="10" y1="14" x2="14" y2="14" />
        </svg>
      );
    case 7: // Consistency - repeat arrows
      return (
        <svg {...props}>
          <polyline points="17 2 21 6 17 10" />
          <path d="M3 11 V9 A4 4 0 0 1 7 5 H21" />
          <polyline points="7 22 3 18 7 14" />
          <path d="M21 13 V15 A4 4 0 0 1 17 19 H3" />
        </svg>
      );
    case 8: // Separation - rectangle divided into two clear halves
      return (
        <svg {...props}>
          <rect x="2" y="5" width="20" height="14" rx="2" />
          <line x1="12" y1="5" x2="12" y2="19" strokeWidth="2.5" />
        </svg>
      );
    case 9: // Time on admin - clock
      return (
        <svg {...props}>
          <circle cx="12" cy="12" r="9" />
          <polyline points="12 7 12 12 15.5 15.5" />
        </svg>
      );
    case 10: // Goal - planted flag
      return (
        <svg {...props}>
          <line x1="5" y1="3" x2="5" y2="21" />
          <path d="M5 3 L19 8 L5 14 Z" fill="currentColor" stroke="none" />
        </svg>
      );
    default:
      return (
        <svg {...props}>
          <circle cx="12" cy="12" r="9" />
          <line x1="12" y1="8" x2="12" y2="12" />
          <line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
      );
  }
}

// ── Main component ─────────────────────────────────────────────────────────────

export default function TaxesQuiz() {
  const { setFlow } = useApp();

  const [phase, setPhase] = useState<Phase>('intro');
  const [currentQ, setCurrentQ] = useState(1);
  const [answers, setAnswers] = useState<Answers>({});
  const [currentSelection, setCurrentSelection] = useState<string | null>(null);
  const [pendingNarration, setPendingNarration] = useState<string | null>(null);
  const [narrationSourceQ, setNarrationSourceQ] = useState(0);
  const [loadingStep, setLoadingStep] = useState(0);
  const [emailValue, setEmailValue] = useState('');
  const [emailError, setEmailError] = useState('');
  const [emailSubmitting, setEmailSubmitting] = useState(false);
  const [capturedEmail, setCapturedEmail] = useState('');
  const [narrationDelayActive, setNarrationDelayActive] = useState(false);

  const stepStartTimeRef = useRef<number>(Date.now());
  const utmRef = useRef<Record<string, string>>({});

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'fbclid', 'utm_landing_page'];
    const utms: Record<string, string> = {};
    utmKeys.forEach((key) => {
      const val = params.get(key);
      if (val) utms[key] = val;
    });
    if (!utms.utm_landing_page) utms.utm_landing_page = 'taxes';
    utmRef.current = utms;

    setFlow('taxes');
    page('Taxes Quiz', { ...utms });
    track(EVENTS.TAXES_QUIZ_SHOWN, { flow: 'taxes', device: getDevice(), ...utms });
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
    stepStartTimeRef.current = Date.now();
  }, [currentQ, phase]);

  useEffect(() => {
    if (phase !== 'quiz') return;
    const key = `q${currentQ}` as keyof Answers;
    setCurrentSelection(answers[key] ?? null);
  }, [currentQ, phase]);

  // Loading animation
  useEffect(() => {
    if (phase !== 'loading') return;
    setLoadingStep(0);
    const timers = [
      setTimeout(() => setLoadingStep(1), 500),
      setTimeout(() => setLoadingStep(2), 1000),
      setTimeout(() => setLoadingStep(3), 1500),
      setTimeout(() => setLoadingStep(4), 2000),
      setTimeout(() => setPhase('email'), 2500),
    ];
    return () => timers.forEach(clearTimeout);
  }, [phase]);

  function handleContinue() {
    if (!currentSelection || narrationDelayActive) return;

    const key = `q${currentQ}` as keyof Answers;
    const newAnswers: Answers = { ...answers, [key]: currentSelection };
    setAnswers(newAnswers);

    const { options } = getQuestionContent(currentQ, newAnswers);
    const selectedOption = options.find((o) => o.id === currentSelection);

    track(EVENTS.TAXES_QUESTION_ANSWERED, {
      flow: 'taxes',
      questionNumber: currentQ,
      questionKey: QUESTION_KEYS[currentQ],
      answerId: currentSelection,
      answerLabel: selectedOption?.title ?? currentSelection,
      score: getScore(currentQ, currentSelection),
      device: getDevice(),
      timeOnQuestionSec: Math.round((Date.now() - stepStartTimeRef.current) / 1000),
      ...utmRef.current,
    });

    if (currentQ === 1) {
      track(EVENTS.TAXES_QUIZ_STARTED, { flow: 'taxes', ...utmRef.current });
      setCurrentQ(2);
      setCurrentSelection(null);
      setPhase('quiz');
      return;
    }

    if (currentQ === 10) {
      // Q10: skip narration, go directly to loading
      track(EVENTS.TAXES_QUIZ_COMPLETED, {
        flow: 'taxes',
        score: getTotalScore(newAnswers),
        segment: newAnswers.q1,
        q10: currentSelection,
        device: getDevice(),
        ...utmRef.current,
      });
      setPhase('loading');
      return;
    }

    if (currentQ === 9) {
      // Q9: skip narration, go directly to insight2
      setCurrentQ(10);
      setCurrentSelection(null);
      setPhase('insight2');
      return;
    }

    // Q4, Q5, Q7, Q8: skip narration, go directly to next question
    if ([4, 5, 7, 8].includes(currentQ)) {
      setCurrentQ((prev) => prev + 1);
      setCurrentSelection(null);
      setPhase('quiz');
      return;
    }

    // Q2, Q3, Q6 get narration (150ms delay)
    const narration = getNarration(currentQ, currentSelection);
    setNarrationDelayActive(true);
    setNarrationSourceQ(currentQ);
    setTimeout(() => {
      setPendingNarration(narration);
      setPhase('narration');
      setNarrationDelayActive(false);
    }, 150);
  }

  function handleNarrationContinue() {
    const sourceQ = narrationSourceQ;
    setPendingNarration(null);

    if (sourceQ === 10) {
      setPhase('loading');
      return;
    }
    if (sourceQ === 3) {
      // Insight card 1 between Q3 and Q4
      setCurrentQ(4);
      setCurrentSelection(null);
      setPhase('insight1');
      return;
    }
    if (sourceQ === 9) {
      // Insight card 2 between Q9 and Q10
      setCurrentQ(10);
      setCurrentSelection(null);
      setPhase('insight2');
      return;
    }
    setCurrentQ((prev) => prev + 1);
    setCurrentSelection(null);
    setPhase('quiz');
  }

  function handleBack() {
    if (phase === 'narration') {
      setPendingNarration(null);
      setPhase('quiz');
      return;
    }
    if (phase === 'insight1') {
      setCurrentQ(3);
      setCurrentSelection(answers.q3 ?? null);
      setPhase('quiz');
      return;
    }
    if (phase === 'insight2') {
      setCurrentQ(9);
      setCurrentSelection(answers.q9 ?? null);
      setPhase('quiz');
      return;
    }
    if (currentQ === 1) {
      setPhase('intro');
      return;
    }
    if (currentQ === 2) {
      setCurrentQ(1);
      setCurrentSelection(answers.q1 ?? null);
      return;
    }
    setCurrentQ((prev) => prev - 1);
    setCurrentSelection(null);
  }

  function handleEmailSubmit(e: React.FormEvent) {
    e.preventDefault();
    const err = validateEmail(emailValue);
    if (err) { setEmailError(err); return; }
    setEmailError('');
    setEmailSubmitting(true);
    identify(emailValue, { flow: 'taxes', ...utmRef.current });
    track(EVENTS.TAXES_AUTH_SUBMITTED, { flow: 'taxes', email: emailValue, ...utmRef.current });
    const score = getTotalScore(answers);
    track(EVENTS.TAXES_RESULT_SHOWN, { flow: 'taxes', score, tier: getTier(score).name, email: emailValue, ...utmRef.current });
    setCapturedEmail(emailValue);
    setEmailSubmitting(false);
    setPhase('results');
  }

  // ── Progress ──────────────────────────────────────────────────────────────────

  const progressPct =
    phase === 'loading' || phase === 'email' || phase === 'results' ? 100
    : phase === 'intro' ? 0
    : Math.round(((currentQ - 1) / TOTAL_QUESTIONS) * 100);

  // ── Shared progress bar header ────────────────────────────────────────────────

  function ProgressHeader({ showBack = false }: { showBack?: boolean }) {
    return (
      <div className="rental-layout__progress">
        <div className="rental-progress">
          <div className="rental-progress__bar">
            <div className="rental-progress__fill" style={{ width: `${progressPct}%` }} />
          </div>
          <div className="rental-progress__meta">
            <span className="rental-progress__pct">{progressPct}%</span>
          </div>
        </div>
        {showBack && (
          <button className="rental-back-btn" onClick={handleBack} aria-label="Go back">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="19" y1="12" x2="5" y2="12" />
              <polyline points="12 19 5 12 12 5" />
            </svg>
            Back
          </button>
        )}
      </div>
    );
  }

  // ── Render: intro ─────────────────────────────────────────────────────────────

  if (phase === 'intro') {
    return (
      <>
        <Helmet>
          <title>Is Your Tax Money Protected? | Sequence</title>
          <meta name="description" content="Answer 11 quick questions and find out exactly where your tax setup stands - and what one change could save you this tax season." />
        </Helmet>
        <div className="taxes-intro">
          <div className="taxes-intro__container">
            <div className="taxes-intro__content">
              <IntroIllustration />
              <h1 className="taxes-intro__headline">
                Some people shrug in April. Find out which one you are.
              </h1>
              <p className="taxes-intro__subheadline">
                10 questions. A score. No accountant required.
              </p>
              <Button
                variant="primary"
                size="lg"
                fullWidth
                onClick={() => {
                  track(EVENTS.TAXES_QUIZ_CTA_CLICKED, { flow: 'taxes', ...utmRef.current });
                  setPhase('quiz');
                }}
                icon={
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="5" y1="12" x2="19" y2="12" />
                    <polyline points="12 5 19 12 12 19" />
                  </svg>
                }
                iconPosition="right"
              >
                Find out in 2 minutes
              </Button>
              <p className="taxes-intro__trust">Free. No account needed.</p>
            </div>
          </div>
        </div>
      </>
    );
  }

  // ── Render: loading ───────────────────────────────────────────────────────────

  if (phase === 'loading') {
    return (
      <>
        <Helmet>
          <title>Analyzing Your Tax Readiness... | Sequence</title>
        </Helmet>
        <div className="taxes-loading">
          <div className="taxes-loading__container">
            <h1 className="taxes-loading__title">We found a gap in your current setup.</h1>
            <p className="taxes-loading__subtitle">
              {loadingStep >= 2 ? 'This is fixable and faster than you think.' : 'Preparing your result...'}
            </p>
            <div className="taxes-loading__checklist">
              {LOADING_ITEMS.map((item, i) => {
                const done = i < loadingStep;
                return (
                  <div key={i} className={`taxes-loading__item${done ? ' taxes-loading__item--done' : i === loadingStep - 1 ? ' taxes-loading__item--active' : ' taxes-loading__item--pending'}`}>
                    <span className="taxes-loading__item-icon">
                      {done ? (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      ) : (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="12" cy="12" r="9" />
                        </svg>
                      )}
                    </span>
                    <span className="taxes-loading__item-text">{item}</span>
                  </div>
                );
              })}
            </div>
            {loadingStep >= LOADING_ITEMS.length && (
              <p className="taxes-loading__body">
                Most people who complete this quiz find at least one gap they didn't know was there - and a clear path to close it before April.
              </p>
            )}
          </div>
        </div>
      </>
    );
  }

  // ── Render: email gate ────────────────────────────────────────────────────────

  if (phase === 'email') {
    const previewScore = getTotalScore(answers);
    return (
      <>
        <Helmet>
          <title>Your Tax Readiness Score is Ready | Sequence</title>
        </Helmet>
        <div className="taxes-email-gate">
          <div className="taxes-email-gate__container">
            <div className="taxes-email-gate__score-preview" aria-hidden="true">
              <div className="taxes-email-gate__score-ring">
                <span className="taxes-email-gate__score-number">{previewScore}</span>
                <span className="taxes-email-gate__score-denom">/ 27</span>
              </div>
              <div className="taxes-email-gate__score-blur-overlay" />
            </div>

            <h1 className="taxes-email-gate__headline">We found a gap in your tax setup</h1>
            <p className="taxes-email-gate__subheadline">
              See what's causing it and how to fix it.
            </p>

            <form className="taxes-email-gate__form" onSubmit={handleEmailSubmit}>
              <div className="taxes-email-gate__field">
                <input
                  type="email"
                  className={`taxes-email-gate__input${emailError ? ' taxes-email-gate__input--error' : ''}`}
                  placeholder="Email address"
                  value={emailValue}
                  onChange={(e) => { setEmailValue(e.target.value); if (emailError) setEmailError(''); }}
                  autoComplete="email"
                  required
                />
                {emailError && <p className="taxes-email-gate__error">{emailError}</p>}
              </div>
              <Button
                type="submit"
                variant="primary"
                size="lg"
                fullWidth
                loading={emailSubmitting}
                icon={
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="5" y1="12" x2="19" y2="12" />
                    <polyline points="12 5 19 12 12 19" />
                  </svg>
                }
                iconPosition="right"
              >
                Show my result
              </Button>
              <p className="taxes-email-gate__trust">
                No spam. Just your result.
              </p>
            </form>
          </div>
        </div>
      </>
    );
  }

  // ── Render: results ───────────────────────────────────────────────────────────

  if (phase === 'results') {
    const score = getTotalScore(answers);
    const tier = getTier(score);
    const gaps = getPersonalizedGaps(answers);
    return (
      <>
        <Helmet>
          <title>Your Tax Readiness Score | Sequence</title>
        </Helmet>
        <div className="taxes-results">
          <div className="taxes-results__container">
            <div className="taxes-results__grid">

              {/* ── Left: Diagnosis ── */}
              <div className="taxes-results__diagnosis">
                <div className="taxes-results__score-row">
                  <span className={`taxes-results__score-num taxes-results__score-num--${tier.color}`}>
                    {score}
                  </span>
                  <div className="taxes-results__score-meta">
                    <span className="taxes-results__score-denom">/ 27</span>
                    <span className="taxes-results__score-sublabel">Tax Readiness Score</span>
                  </div>
                </div>
                <span className={`taxes-results__tier-badge taxes-results__tier-badge--${tier.color}`}>
                  {tier.name}
                </span>
                <h1 className="taxes-results__diag-headline">{tier.diagnosisHeadline}</h1>
                <div className="taxes-results__diag-body">
                  {tier.diagnosisBody.map((para, i) => (
                    <p key={i} className="taxes-results__diag-para">{para}</p>
                  ))}
                </div>
                {gaps.length > 0 && (
                  <div className="taxes-results__gaps">
                    <p className="taxes-results__gaps-label">Based on your answers:</p>
                    <ul className="taxes-results__gaps-list">
                      {gaps.map((gap, i) => (
                        <li key={i} className="taxes-results__gap-item">{gap}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {/* ── Right: Sequence promo ── */}
              <div className="taxes-results__promo">
                <p className="taxes-results__promo-intro">{tier.promoIntro}</p>
                <p className="taxes-results__promo-desc">{tier.promoDesc}</p>
                <p className="taxes-results__promo-tagline">{tier.promoTagline}</p>
                <div className="taxes-results__changes">
                  <p className="taxes-results__changes-label">What changes</p>
                  <ul className="taxes-results__changes-list">
                    {tier.promoBullets.map((bullet, i) => (
                      <li key={i} className="taxes-results__change-item">{bullet}</li>
                    ))}
                  </ul>
                </div>
                <Button
                  variant="primary"
                  size="lg"
                  fullWidth
                  className="taxes-results__promo-cta"
                  onClick={() => {
                    track(EVENTS.SEQUENCE_SIGNUP_CLICKED, { flow: 'taxes', tier: tier.name, score, ...utmRef.current });
                    window.location.href = buildTaxesSignupUrl({ segment: answers.q1, q11: answers.q10, email: capturedEmail, utms: utmRef.current });
                  }}
                  icon={
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="5" y1="12" x2="19" y2="12" />
                      <polyline points="12 5 19 12 12 19" />
                    </svg>
                  }
                  iconPosition="right"
                >
                  {tier.promoCta}
                </Button>
                <div className="taxes-results__promo-meta">
                  <span>Takes ~10 minutes</span>
                  <span>No impact on your existing accounts</span>
                  <span>Cancel anytime</span>
                </div>
              </div>

            </div>
          </div>
        </div>
      </>
    );
  }

  // ── Render: insight cards ─────────────────────────────────────────────────────

  if (phase === 'insight1' || phase === 'insight2') {
    const isFirst = phase === 'insight1';
    return (
      <>
        <Helmet>
          <title>Is Your Tax Money Protected? | Sequence</title>
        </Helmet>
        <div className="rental-layout">
          <ProgressHeader showBack />
          <main className="rental-layout__content">
            <div className="rental-layout__container">
              <div className="taxes-insight">
                <div className="taxes-insight__illustration">
                  {isFirst ? <Insight1Illustration /> : <Insight2Illustration />}
                </div>
                <p className="taxes-insight__stat">
                  {isFirst
                    ? 'Most self-employed people underestimate their tax bill by 20-35%.'
                    : 'The people who describe tax season as "calm" all have one thing in common.'}
                </p>
                <p className="taxes-insight__body">
                  {isFirst
                    ? "Not because they don't care - because they're working from memory and guesswork instead of a system that responds to every deposit automatically."
                    : 'Their money moves before they can spend it. Not through discipline - through automation.'}
                </p>
                <p className="taxes-insight__subline">
                  {isFirst
                    ? 'This quiz shows you exactly where that gap is in your setup.'
                    : 'Two more questions and we\'ll show you exactly where your setup stands.'}
                </p>
              </div>
            </div>
          </main>
          <div className="rental-layout__navigation">
            <div className="rental-layout__nav-container">
              <StepNavigation
                onBack={handleBack}
                onContinue={() => setPhase('quiz')}
                canContinue
                continueLabel="Continue"
                showBack={false}
              />
            </div>
          </div>
        </div>
      </>
    );
  }

  // ── Render: narration card ────────────────────────────────────────────────────

  if (phase === 'narration') {
    return (
      <>
        <Helmet>
          <title>Is Your Tax Money Protected? | Sequence</title>
        </Helmet>
        <div className="rental-layout">
          <ProgressHeader showBack />
          <main className="rental-layout__content">
            <div className="rental-layout__container">
              <div className="taxes-narration">
                <div className="taxes-narration__accent">
                  {getNarrationIcon(narrationSourceQ)}
                </div>
                <p className="taxes-narration__text">{pendingNarration}</p>
              </div>
            </div>
          </main>
          <div className="rental-layout__navigation">
            <div className="rental-layout__nav-container">
              <StepNavigation
                onBack={handleBack}
                onContinue={handleNarrationContinue}
                canContinue
                continueLabel={narrationSourceQ === 10 ? 'See My Score' : 'Next'}
                showBack={false}
              />
            </div>
          </div>
        </div>
      </>
    );
  }

  // ── Render: quiz question ─────────────────────────────────────────────────────

  const { question, helper, options } = getQuestionContent(currentQ, answers);

  return (
    <>
      <Helmet>
        <title>Is Your Tax Money Protected? | Sequence</title>
        <meta name="description" content="Answer 10 quick questions and find out exactly where your tax setup stands." />
      </Helmet>
      <div className="rental-layout">
        <ProgressHeader showBack={currentQ > 1} />
        <main className="rental-layout__content">
          <div className="rental-layout__container">
            <div className="quiz-step">
              <div className="quiz-step__header">
                <h2 className="quiz-step__question">{question}</h2>
                {helper && <p className="quiz-step__helper">{helper}</p>}
              </div>
              <div className="quiz-step__body">
                <SingleSelectCards
                  options={options}
                  value={currentSelection}
                  onChange={setCurrentSelection}
                />
              </div>
              {currentQ === 1 && (
                <p className="rental-quiz__trust-line">Free. No card required. Takes about 2 minutes.</p>
              )}
            </div>
          </div>
        </main>
        <div className="rental-layout__navigation">
          <div className="rental-layout__nav-container">
            <StepNavigation
              onBack={handleBack}
              onContinue={handleContinue}
              canContinue={currentSelection !== null && !narrationDelayActive}
              continueLabel={currentQ === 10 ? 'See My Results' : 'Continue'}
              showBack={false}
            />
          </div>
        </div>
      </div>
    </>
  );
}
